import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_YouTube = () => {
  return (
    <div className="html-container">
      <h1>HTML YouTube – Embedding Videos</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          You can easily <strong>embed YouTube videos</strong> on a webpage using the
          <code>&lt;iframe&gt;</code> element. YouTube provides an embed code that can be
          inserted directly into your HTML document.
        </p>

        <p>
          Embedding videos allows you to display rich multimedia content from
          YouTube without requiring users to leave your website.
        </p>
      </section>

      {/* 2. Basic YouTube Embed */}
      <section className="html-section">
        <h2>2. Basic YouTube Embed Example</h2>
        <p>Here’s the simplest example of embedding a YouTube video:</p>

        <pre className="code-block">
          <code>{`<iframe width="560" height="315"
  src="https://www.youtube.com/embed/tgbNymZ7vqY"
  title="YouTube video player"
  frameborder="0"
  allowfullscreen>
</iframe>`}</code>
        </pre>

        <p>
          The <code>src</code> attribute contains the video URL in embed format.
          The <code>allowfullscreen</code> attribute lets users watch in full screen.
        </p>
      </section>

      {/* 3. Responsive YouTube Video */}
      <section className="html-section">
        <h2>3. Making the YouTube Video Responsive</h2>
        <p>
          To make the embedded video responsive (fit automatically on all screen sizes),
          wrap it in a container with CSS that maintains the correct aspect ratio.
        </p>

        <pre className="code-block">
          <code>{`<div class="video-container">
  <iframe
    src="https://www.youtube.com/embed/tgbNymZ7vqY"
    title="Responsive YouTube video"
    allowfullscreen>
  </iframe>
</div>`}</code>
        </pre>

        <pre className="code-block">
          <code>{`/* CSS Example */
.video-container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  max-width: 100%;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}`}</code>
        </pre>
      </section>

      {/* 4. Autoplay, Mute, and Controls */}
      <section className="html-section">
        <h2>4. Autoplay, Mute, and Controls</h2>
        <p>
          YouTube’s embed URLs support query parameters that control playback behavior.
        </p>

        <table className="styled-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Description</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>?autoplay=1</code></td>
              <td>Starts the video automatically.</td>
              <td><code>https://www.youtube.com/embed/tgbNymZ7vqY?autoplay=1</code></td>
            </tr>
            <tr>
              <td><code>?mute=1</code></td>
              <td>Mutes the video sound (required for autoplay in most browsers).</td>
              <td><code>https://www.youtube.com/embed/tgbNymZ7vqY?mute=1</code></td>
            </tr>
            <tr>
              <td><code>?controls=0</code></td>
              <td>Hides the video controls (play, pause, etc.).</td>
              <td><code>https://www.youtube.com/embed/tgbNymZ7vqY?controls=0</code></td>
            </tr>
          </tbody>
        </table>

        <pre className="code-block">
          <code>{`<iframe width="560" height="315"
  src="https://www.youtube.com/embed/tgbNymZ7vqY?autoplay=1&mute=1"
  title="Autoplay Example"
  frameborder="0"
  allow="autoplay; encrypted-media"
  allowfullscreen>
</iframe>`}</code>
        </pre>
      </section>

      {/* 5. Playlist Embedding */}
      <section className="html-section">
        <h2>5. Embedding a YouTube Playlist</h2>
        <p>
          You can also embed an entire YouTube playlist using the
          <code>list</code> parameter.
        </p>

        <pre className="code-block">
          <code>{`<iframe width="560" height="315"
  src="https://www.youtube.com/embed/videoseries?list=PL9bw4S5ePsEEp5Tcd5iVtV7l8Zbt"
  title="YouTube Playlist"
  frameborder="0"
  allowfullscreen>
</iframe>`}</code>
        </pre>
      </section>

      {/* 6. Embedding Specific Start & End Times */}
      <section className="html-section">
        <h2>6. Embedding Specific Start & End Times</h2>
        <p>
          You can start or stop playback at specific times using
          <code>start</code> and <code>end</code> parameters (in seconds).
        </p>

        <pre className="code-block">
          <code>{`<iframe width="560" height="315"
  src="https://www.youtube.com/embed/tgbNymZ7vqY?start=30&end=60"
  title="Start and End Example"
  frameborder="0"
  allowfullscreen>
</iframe>`}</code>
        </pre>
      </section>

      {/* 7. Embedding with Privacy Mode */}
      <section className="html-section">
        <h2>7. YouTube Privacy-Enhanced Mode</h2>
        <p>
          To respect user privacy, use the{" "}
          <strong>privacy-enhanced</strong> YouTube domain:
          <code>youtube-nocookie.com</code>. This prevents YouTube from storing
          cookies unless the user interacts with the video.
        </p>

        <pre className="code-block">
          <code>{`<iframe width="560" height="315"
  src="https://www.youtube-nocookie.com/embed/tgbNymZ7vqY"
  title="Privacy Mode Video"
  frameborder="0"
  allowfullscreen>
</iframe>`}</code>
        </pre>
      </section>

      {/* 8. Attributes Overview */}
      <section className="html-section">
        <h2>8. Common Attributes</h2>
        <ul>
          <li><code>width</code> and <code>height</code> – Set the video frame size.</li>
          <li><code>src</code> – The embed link to the YouTube video.</li>
          <li><code>title</code> – Describes the video for accessibility.</li>
          <li><code>frameborder</code> – Defines the border width (use “0” for none).</li>
          <li><code>allowfullscreen</code> – Enables fullscreen mode.</li>
          <li><code>allow</code> – Enables features like autoplay, clipboard access, etc.</li>
        </ul>
      </section>

      {/* 9. Best Practices */}
      <section className="html-section">
        <h2>9. Best Practices</h2>
        <ul>
          <li>Always use HTTPS URLs for embeds.</li>
          <li>Use responsive CSS for mobile-friendly videos.</li>
          <li>Consider the user experience before using autoplay.</li>
          <li>Prefer privacy-enhanced embedding with <code>youtube-nocookie.com</code>.</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>Embed YouTube videos using the <code>&lt;iframe&gt;</code> tag.</li>
          <li>Use parameters to control playback (autoplay, mute, start, end, etc.).</li>
          <li>Apply CSS for responsiveness.</li>
          <li>Use <code>youtube-nocookie.com</code> for privacy-enhanced embeds.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_YouTube;
